import {call, fork, put, select} from "redux-saga/effects";
import {IApplicationState} from "../../index";
import {processLevelComplete} from "./processLevelComplete";
import {showAlert} from "../../dialog-manager/dialogs/showAlert";
import {setLevelProgress} from "../actions";
import {trackAction} from "../../common/actions";
import {revealConnectedTiles} from "./tileHandlers/processConnectedTiles";
import {ILevelMap, ILevelMapProgress, ILevelMapTileProgress} from "../types";
import makeMap from "../../../shared/utils/makeMap";
import {trackError} from "../../log/actions";
import i18n from "../../../shared/i18n";

type StateSlice = {
    levelMap: ILevelMap;
    levelMapProgress: ILevelMapProgress;
    levelMapTileProgress: Record<number, ILevelMapTileProgress>;
    exitTileProgress?: ILevelMapTileProgress;
};

export default function* validateLevelMap(levelId: number) {
    try {
        const {levelMap, levelMapProgress, levelMapTileProgress, exitTileProgress}: StateSlice = yield select(({ game }: IApplicationState) => {
            const levelMap = game.levelMaps[levelId];

            return {
                levelMap: levelMap,
                levelMapProgress: game.levelMapProgress[levelId] || {},
                levelMapTileProgress: game.levelMapTileProgress,
                exitTileProgress: levelMap
                    ? game.levelMapTileProgress[levelMap.exitTileId]
                    : undefined,
            };
        });

        if (!levelMap)
            return;

        if (exitTileProgress && exitTileProgress.isAnswered) {
            yield put(trackAction({
                category: "interaction",
                object: "message",
                name: "show",
                addition: "gameplay_levelmap_finalquestionansweredduringprevi",
            }));

            yield showAlert(i18n("gameplay.levelmap.finalquestionansweredduringprevioussession"));

            yield fork(processLevelComplete, levelId, 0);

            return;
        }

        const map = levelMap.tiles.map(row => {
            return row.map(tile => ({
                ...tile,
                ...levelMapTileProgress[tile.id]
            }));
        });

        const answeredTiles = map
            .flatMap(row => row)
            .filter(tile => !!tile.isAnswered)
            .map(tile => tile);

        if (answeredTiles.length) {
            const unrevealedTiles = map.flatMap(row => row).filter(tile => tile.tileType && !tile.isRevealed);

            let connectedTilesProgress: ILevelMapTileProgress[] = [];

            for (let tile of answeredTiles) {
                const tileProgress = yield call(revealConnectedTiles, tile, map);
                connectedTilesProgress = connectedTilesProgress.concat(tileProgress);
            }

            if (connectedTilesProgress.length) {
                const isExitFound = levelMapProgress.isExitFound
                    || connectedTilesProgress.some((tile: ILevelMapTileProgress) => tile.id === levelMap.exitTileId);

                const allTilesAreRevealed = levelMapProgress.allTilesAreRevealed
                    || !unrevealedTiles.some(unrevealedTile => !connectedTilesProgress.some((tile: ILevelMapTileProgress) => tile.id === unrevealedTile.id));

                yield put(setLevelProgress({
                    isExitFound: !!isExitFound,
                    allTilesAreRevealed: !!allTilesAreRevealed,
                    levelProgress: makeMap(connectedTilesProgress, "id")
                }));
            }
        }
    }
    catch (error) {
        yield put(trackError({
            category: "validateLevelMap",
            message: "Failed to validate level map",
            error,
            data: {
                levelId
            }
        }));
    }
}